import { action, makeAutoObservable, observable } from "mobx"
import { ValueStore } from "."
import ContentBlock from "./block"

class Value {

  children = observable.array<Value | Value[]>([])
  contentBlock: ContentBlock
  value: string = ""

  constructor(contentBlock: ContentBlock, valueStore: ValueStore = {}) {
    this.contentBlock = contentBlock
    if (this.contentBlock.enabled === false) {
      this.value = ""
      return
    }
    const value = valueStore[contentBlock.blockId] as string

    if (this.type === "constant") {
      this.value = this.contentBlock.block.data.value
    }
    if (this.type === "value") {
      this.value = value || this.contentBlock.block.data.defaultValue || ""
    }
    if (this.type === "selectable") {
      this.value = value || this.contentBlock.block.emitters[0]
    }
    if (this.type === "array") {
      const count = parseInt(this.contentBlock.block.data.count)
      const value = valueStore[contentBlock.blockId] as ValueStore[]
      if (!isNaN(count)) {
        this.value = count.toString()
      } else {
        this.value = Array.isArray(value)? value.length.toString(): (value || "")
      }
    }
    this.generate(valueStore)

    makeAutoObservable(this, {
      set: action
    })
  }

  get type() {
    return this.contentBlock.block.type
  }

  save(): [ string, string | ValueStore[] ][] {

    const val: [ string, string | ValueStore[] ] = [ this.contentBlock.blockId, this.value ]
    if (this.type === "selectable" || this.type === "group") {
      const arr = val[0]? [ val ]: []
      for (let child of this.children as Value[]) {
        arr.push(...child.save())
      }
      return arr
    }

    if (this.type === "array" && this.children.length > 0) {
      const ch: ValueStore[] = (this.children as Value[][])
        .map(group => Object.fromEntries(group.map(item => item.save()).flat(1)))
        
      return [ [ this.contentBlock.blockId, ch ] ]
    }

    return [ val ]
  }
  
  set(value: string) {
    if (value === this.value) return
    this.value = value.replace(/[\u2028-\u2029]/g, "\n").replace(/[\u2000-\u200F]/g, '')
    const maxSize = this.contentBlock.block.data.maxSize && parseInt(this.contentBlock.block.data.maxSize)
    if (typeof maxSize === "number" && !isNaN(maxSize)) {
      if ((this.type === "array" || (this.type === "value" && this.contentBlock.block.data.dataType === "number")) && parseInt(this.value) > maxSize) {
        this.value = maxSize.toString()
      } else if (this.type === "value" && this.value.length > maxSize) {
        this.value = this.value.slice(0, maxSize)
      }
    }
    this.generate()
  }

  generate(valueStore?: ValueStore) {
    if (this.type === "selectable") {
      const index = Math.max(this.contentBlock.block.emitters.findIndex(e => e === this.value), 0)
      const children = this.contentBlock.children[index]
      this.children.replace(children.map(block => new Value(block, valueStore)))
    }
    if (this.type === "group") {
      const children = this.contentBlock.children[0]
      this.children.replace(children.map(block => new Value(block, valueStore)))
    }
    if (this.type === "array") {
      const v = valueStore && valueStore[this.contentBlock.blockId]
      const val = Array.isArray(v)? v: undefined
      
      const count = Math.min(parseInt(this.value) || 0, 40)
      if (this.children.length > count) {
        this.children.splice(count, this.children.length-count)
      }
      if (this.children.length < count) {
        for (let i = this.children.length; i < count; i++) {
          this.children.push(this.contentBlock.children[0].map(block => new Value(block, val && val[i])))
        }
      }
    }
  }

  private getArrKey(contentBlock: ContentBlock) {
    const key = contentBlock.block.data.key
    if (!key) return key
    if (key.endsWith('s') && contentBlock.block.data.keepS !== "true") return key.slice(0, -1)
    return key
  }
  
  static getStartIndex(contentBlock: ContentBlock) {
    if (contentBlock.block.data.startIndex) {
      const index = parseInt(contentBlock.block.data.startIndex)
      if (isNaN(index)) return 1
      return index
    }
    return 1
  }

  get keyValue() {
    const val = [
      this.contentBlock.block.data.key || "",
      this.value
    ]

    if (this.type === "array") {
      const arr: string[][] = []
      if (!val[1]) return arr

      const arrKey = this.getArrKey(this.contentBlock)
      if (val[0] && !this.contentBlock.block.data.hideCount) {
        val[0] = val[0] + ".count"
        arr.push(val)
      }
      const startIndex = Value.getStartIndex(this.contentBlock)
      for (let i = 0; i < this.children.length; i++) {
        const child = this.children[i] as Value[]
        for (let item of child) {
          arr.push(...item.keyValue.map(val => [ join(arrKey, i+startIndex, val[0]), val[1] ]))
        }
      }
      arr.unshift()
      return arr
    }

    if (this.type === "selectable" || this.type === "group") {
      const useAsKey = (this.type === "group" && this.contentBlock.block.data.key) || (this.type === "selectable" && this.contentBlock.block.data.useAsKey === "true")
      const arr = (val[0] && !useAsKey)? [ val ]: []
      for (let item of this.children as Value[]) {
        arr.push(...item.keyValue.map(val => {
          if (this.type === "group" && this.contentBlock.block.data.changeSlug) {
            return [ `$_${this.contentBlock.block.data.changeSlug}.` + val[0], val[1] ]
          }
          if (useAsKey) {
            return [ join(this.contentBlock.block.data.key, val[0]), val[1] ]
          } 
          return [ val[0], val[1] ]
        }))
      }
      return arr
    }

    if (!val[1]) return []

    if ((this.type === "value" || this.type === "constant") && this.value.includes("\n")) {
      val[1] = val[1].replaceAll("\n", "\\n")
    }

    return [ val ]
  }

}

export default Value

const join = (...val: (string | number)[]) => {
  return val.filter(key => !!key).join(".")
}